import { Navigate, Route, Routes } from 'react-router-dom';
import { RedirectIfAuthenticated, RequireAuth } from './auth/guards';
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { AccountSecurity } from './pages/account/AccountSecurity';
import { Challenge } from './pages/auth/Challenge';
import { ConfirmSignUp } from './pages/auth/ConfirmSignUp';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { PhoneSignIn } from './pages/auth/PhoneSignIn';
import { ResetPassword } from './pages/auth/ResetPassword';
import { SignIn } from './pages/auth/SignIn';
import { SignUp } from './pages/auth/SignUp';
import { Dashboard } from './pages/Dashboard';
import { Deactivated } from './pages/Deactivated';
import { TenantDetail } from './pages/TenantDetail';
import { Email } from './pages/portal/Email';
import { Subscription } from './pages/portal/Subscription';
import { TenantDashboard } from './pages/portal/TenantDashboard';
import { WhatsApp } from './pages/portal/WhatsApp';
import { EmailLab } from './pages/staff/EmailLab';
import { StaffTeam } from './pages/staff/StaffTeam';
import { StaffDashboard } from './pages/staff/StaffDashboard';
import { Status } from './pages/staff/Status';
import { TenantPage } from './pages/staff/TenantPage';
import { Tenants } from './pages/staff/Tenants';
import { Users } from './pages/staff/Users';

/**
 * Public auth pages live under <AuthLayout>; everything else needs a session
 * (<RequireAuth>) and renders inside the signed-in <AppLayout>. /staff is
 * staff-only (/staff/users admin-only) and /portal tenant-only; /dashboard
 * picks the right one. /tenant/:tenantId is for both: staff open any tenant,
 * a tenant account its own.
 */
export function App() {
  return (
    <Routes>
      <Route element={<RedirectIfAuthenticated />}>
        <Route element={<AuthLayout />}>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-in/phone" element={<PhoneSignIn />} />
          <Route path="/sign-in/challenge" element={<Challenge />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-up/confirm" element={<ConfirmSignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>
      </Route>

      <Route element={<RequireAuth />}>
        <Route path="/deactivated" element={<Deactivated />} />
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/account" element={<AccountSecurity />} />
          <Route path="/tenant/:tenantId" element={<TenantDetail />} />
          <Route element={<RequireAuth role="staff" />}>
            <Route path="/staff" element={<StaffDashboard />} />
            <Route path="/staff/tenants" element={<Tenants />} />
            <Route path="/staff/team" element={<StaffTeam />} />
            <Route path="/staff/status" element={<Status />} />
            <Route path="/staff/email-lab" element={<EmailLab />} />
            <Route element={<RequireAuth access="admin" />}>
              <Route path="/staff/users" element={<Users />} />
            </Route>
            {/* Last on purpose: a tenant's slug, redirected to /tenant/<id>. Static paths above rank ahead of it. */}
            <Route path="/:slug" element={<TenantPage />} />
          </Route>
          <Route element={<RequireAuth role="tenant" />}>
            <Route path="/portal" element={<TenantDashboard />} />
            <Route path="/portal/email" element={<Email />} />
            <Route path="/portal/whatsapp" element={<WhatsApp />} />
            <Route path="/portal/subscription" element={<Subscription />} />
            <Route path="/portal/plans" element={<Navigate to="/portal/subscription" replace />} />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
